"use client";

import { c as _c } from "react/compiler-runtime";
import { graphql, useMutation, useQuery } from "@opensea/graphql";
import { TimelineSkeleton } from "@opensea/ui-kit";
import { useCallback } from "react";
import { ActionTimeline, ActionTimelineError } from "../../../../../components/ActionTimeline";
import { ActionTimelineFragment } from "../../../../../components/ActionTimeline/ActionTimeline.fragment";
import { useAddress } from "../../../../../polychain/hooks";
import { useTrackError } from "../../../../../providers/TrackErrorProvider";
import { useActionErrors } from "../../../../hooks/useErrors";
import { useSetBlockchainActionErrors } from "../../../../hooks/useSetBlockchainActionErrors";
import { useSetBlockchainActionErrorsFragment } from "../../../../hooks/useSetBlockchainActionErrors.fragment";
import { readOrderCreationError } from "../../../../utils/readOrderCreationError";
import { readOrderCreationErrorFragment } from "../../../../utils/readOrderCreationError.fragment";
import { seaportOrder } from "../../../../utils/seaportOrder";
import { seaportOrderFragment } from "../../../../utils/seaportOrder.fragment";
export const CreateOfferTimeline = t0 => {
  const $ = _c(32);
  const {
    offers,
    onError,
    onSuccess,
    onBack
  } = t0;
  const address = useAddress();
  const t1 = address;
  let t2;
  if ($[0] !== offers) {
    t2 = offers.map(_temp);
    $[0] = offers;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      address: t1,
      offers: t2
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = !address;
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = {
      query: CreateOfferTimelineQuery,
      variables: t3,
      pause: t4
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const [t6, refetch] = useQuery(t5);
  const {
    data,
    error
  } = t6;
  const trackError = useTrackError();
  const [, mutate] = useMutation(CreateOfferTimelineMutation);
  const actions = data?.createOffersActions.actions;
  let t7;
  if ($[8] !== actions || $[9] !== address || $[10] !== mutate || $[11] !== offers || $[12] !== onError || $[13] !== onSuccess || $[14] !== trackError) {
    t7 = async response => {
      if (response.action !== "SIGNATURE") {
        throw new Error("No signature response found from scheduled actions");
      }
      const actionWithOrders = actions?.find(_temp2);
      if (actionWithOrders?.__typename === "CreateOffersAction") {
        const orders = actionWithOrders.orders.map(_temp3);
        const chain = actionWithOrders.items[0].chain.identifier;
        const mutation = await mutate({
          orders,
          chain,
          signature: response.signature
        });
        const dataError = readOrderCreationError(mutation.data?.createOffers);
        if (mutation.error) {
          trackError(mutation.error, {
            extra: {
              offers
            },
            tags: {
              flow: "create-offer",
              address: address || ""
            }
          });
          onError?.(new Error(mutation.error.message));
        } else {
          if (dataError) {
            onError?.(new Error(dataError.message));
          } else {
            onSuccess?.();
          }
        }
      }
    };
    $[8] = actions;
    $[9] = address;
    $[10] = mutate;
    $[11] = offers;
    $[12] = onError;
    $[13] = onSuccess;
    $[14] = trackError;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const onDone = t7;
  useSetBlockchainActionErrors(data?.createOffersActions.errors);
  const actionError = useActionErrors();
  if (!actions) {
    let t8;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <TimelineSkeleton />;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  if (error) {
    let t8;
    if ($[17] !== refetch) {
      t8 = () => refetch({
        requestPolicy: "network-only"
      });
      $[17] = refetch;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    let t9;
    if ($[19] !== onBack || $[20] !== t8) {
      t9 = <ActionTimelineError message="Something went wrong" onBack={onBack} onRetry={t8} />;
      $[19] = onBack;
      $[20] = t8;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    return t9;
  }
  if (actionError) {
    let t8;
    if ($[22] !== refetch) {
      t8 = () => refetch({
        requestPolicy: "network-only"
      });
      $[22] = refetch;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    let t9;
    if ($[24] !== actionError || $[25] !== onBack || $[26] !== t8) {
      t9 = <ActionTimelineError message={actionError} onBack={onBack} onRetry={t8} />;
      $[24] = actionError;
      $[25] = onBack;
      $[26] = t8;
      $[27] = t9;
    } else {
      t9 = $[27];
    }
    return t9;
  }
  let t8;
  if ($[28] !== actions || $[29] !== onBack || $[30] !== onDone) {
    t8 = <ActionTimeline actions={actions} onAllActionsCompleted={onDone} onBack={onBack} />;
    $[28] = actions;
    $[29] = onBack;
    $[30] = onDone;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  return t8;
};
const CreateOfferTimelineQuery = graphql(`
    query CreateOfferTimelineQuery(
      $offers: [ItemOfferInput!]!
      $address: Address!
    ) {
      createOffersActions(
        offers: $offers
        address: $address
      ) {
        actions {
          __typename
          ... on CreateOffersAction {
            items {
              chain {
                identifier
              }
            }
            orders {
              ...seaportOrder
            }
          }
          ...ActionTimeline
        }
        errors {
          ...useSetBlockchainActionErrors
        }
      }
    }
  `, [ActionTimelineFragment, seaportOrderFragment, useSetBlockchainActionErrorsFragment]);
const CreateOfferTimelineMutation = graphql(`
    mutation CreateOfferTimelineMutation(
      $chain: ChainIdentifier!
      $orders: [SeaportOrderComponentsInput!]!
      $signature: String!
    ) {
      createOffers(chain: $chain, orders: $orders, signature: $signature) {
        ...readOrderCreationError
      }
    }
  `, [readOrderCreationErrorFragment]);
function _temp(offer) {
  return {
    item: {
      tokenId: offer.item.tokenId,
      contractAddress: offer.item.contractAddress,
      chain: offer.item.chain.identifier
    },
    quantity: offer.quantity,
    pricePerItem: {
      unit: offer.pricePerItem.unit.toString(),
      contractAddress: offer.pricePerItem.contractAddress
    },
    startTime: offer.startTime.toISOString(),
    endTime: offer.endTime.toISOString()
  };
}
function _temp2(action) {
  return action.__typename === "CreateOffersAction";
}
function _temp3(order) {
  return seaportOrder(order);
}